// Calculo de asistencia, descuentos y planilla.
//
// Vive fuera de los componentes a proposito: el Resumen de Control de Personal
// y el modulo de Nomina tienen que dar exactamente el mismo numero. Cuando esta
// logica estaba duplicada ya se separo una vez (un sitio contaba el sabado como
// laborable y el otro no) y el descuento salia inflado.
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';

const DEFAULT_ENTRY = '09:00';
const DEFAULT_EXIT = '18:00';
const DEFAULT_WORK_HOURS = 8; // jornada por defecto: 9h de horario menos 1h de refrigerio

// ── Helpers ──
const calculateHours = (start, end) => {
    if (!start || !end) return 0;
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    let diff = (endH * 60 + endM) - (startH * 60 + startM);
    if (diff < 0) diff += 24 * 60;
    return Number((diff / 60).toFixed(2));
};

// ── Refrigerio (descanso no pagado) ─────────────────────────────────
// Antes se restaba 1 hora fija y escondida al calcular las horas del horario,
// pero las horas realmente trabajadas nunca lo descontaban. Ahora el refrigerio
// es un valor configurable (global, con excepción por persona) y se aplica en
// los dos sitios, así los números cuadran entre sí.
const DEFAULT_BREAK_MINUTES = 60;
const DEFAULT_MIN_WAGE = 1130; // RMV Perú, sólo como referencia
const DEFAULT_WORK_DAYS = [1, 2, 3, 4, 5, 6]; // 0 = domingo. Sábado sí se trabaja.
const DAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const DAY_INITIALS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

// Tipos de jornada. 'auto' la deduce del horario; los demás fijan las horas
// que se usan para prorratear el sueldo y calcular la tarifa/hora.
const WORKDAY_TYPES = [
    { id: 'auto', label: 'Según horario' },
    { id: 'completa', label: 'Completa · 8h', hours: 8 },
    { id: 'media', label: 'Media · 4h', hours: 4 },
    { id: 'custom', label: 'Personalizada' },
];

// Los formularios devuelven strings; sin esto un "0" escrito a mano se
// ignoraba y se aplicaba el refrigerio global por error.
const toNumber = (value) => {
    if (typeof value === 'number') return isNaN(value) ? null : value;
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value);
        return isNaN(n) ? null : n;
    }
    return null;
};

// Minutos de refrigerio. `hasBreak: false` manda: quien no hace pausa no
// pierde tiempo aunque el workspace tenga un valor por defecto.
const getBreakMinutes = (settings, global) => {
    if (settings && settings.hasBreak === false) return 0;
    const own = toNumber(settings?.breakMinutes);
    if (own !== null) return Math.max(0, own);
    const g = toNumber(global?.defaultBreakMinutes);
    if (g !== null) return Math.max(0, g);
    return DEFAULT_BREAK_MINUTES;
};

// ── Feriados nacionales de Perú ─────────────────────────────────────
// Fechas fijas + Semana Santa, calculada del domingo de Pascua. Lo que agregue
// el admin en el Calendario laboral se guarda en overtime_global.customHolidays.
const FIXED_HOLIDAYS = [
    ['01-01', 'Año Nuevo'],
    ['05-01', 'Día del Trabajo'],
    ['06-07', 'Batalla de Arica'],
    ['06-29', 'San Pedro y San Pablo'],
    ['07-23', 'Día de la Fuerza Aérea'],
    ['07-28', 'Fiestas Patrias'],
    ['07-29', 'Fiestas Patrias'],
    ['08-06', 'Batalla de Junín'],
    ['08-30', 'Santa Rosa de Lima'],
    ['10-08', 'Combate de Angamos'],
    ['11-01', 'Todos los Santos'],
    ['12-08', 'Inmaculada Concepción'],
    ['12-09', 'Batalla de Ayacucho'],
    ['12-25', 'Navidad'],
];

// Domingo de Pascua (algoritmo gregoriano anónimo).
const easterSunday = (year) => {
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
};

const getPeruHolidays = (year) => {
    const easter = easterSunday(year);
    const offset = (days) => {
        const d = new Date(easter);
        d.setDate(d.getDate() + days);
        return format(d, 'yyyy-MM-dd');
    };
    return [
        ...FIXED_HOLIDAYS.map(([md, name]) => ({ date: `${year}-${md}`, name })),
        { date: offset(-3), name: 'Jueves Santo' },
        { date: offset(-2), name: 'Viernes Santo' },
    ].sort((x, y) => x.date.localeCompare(y.date));
};

// Nombre del feriado en esa fecha, o null. Lo agregado por el admin manda.
const getHolidayName = (dateStr, global) => {
    if (!dateStr) return null;
    const custom = (global?.customHolidays || []).find(h => h.date === dateStr);
    if (custom) return custom.name;
    const found = getPeruHolidays(Number(dateStr.slice(0, 4))).find(h => h.date === dateStr);
    return found ? found.name : null;
};

// Feriado trabajado: además del sueldo mensual, que ya incluye ese día, se paga
// un día más (pago doble). Es proporcional a lo trabajado respecto de la jornada,
// con tope de un día; sin salida marcada no se puede calcular y queda en cero.
const computeHolidayWork = ({ records = [], global, workHrsPerDay = 0, dayRate = 0 }) => {
    const byDate = new Map();
    records.forEach(r => {
        if (!r.entryTime || r.status === 'falta') return;
        const name = getHolidayName(r.date, global);
        if (!name) return;
        const hours = r.workedHours || 0;
        const prev = byDate.get(r.date);
        if (!prev || hours > prev.hours) byDate.set(r.date, { date: r.date, name, hours, pendingExit: !r.exitTime });
    });
    const items = [...byDate.values()].map(it => {
        const fraction = workHrsPerDay > 0 ? Math.min(1, it.hours / workHrsPerDay) : 0;
        return { ...it, fraction, amount: fraction * dayRate };
    }).sort((a, b) => b.date.localeCompare(a.date));
    return { items, total: items.reduce((a, it) => a + it.amount, 0) };
};

// Horas del horario pactado, ya sin refrigerio.
const scheduleNetHours = (settings, global) => {
    const gross = calculateHours(settings?.scheduledEntry || DEFAULT_ENTRY, settings?.scheduledExit || DEFAULT_EXIT);
    const net = gross - getBreakMinutes(settings, global) / 60;
    return net > 0 ? Number(net.toFixed(2)) : gross;
};

// Horas efectivas por día: base del prorrateo del sueldo y de la tarifa/hora.
// Si hay un tipo de jornada fijado, manda sobre lo que diga el horario.
const effectiveDailyHours = (settings, global) => {
    const type = settings?.workdayType || 'auto';
    if (type === 'completa') return 8;
    if (type === 'media') return 4;
    if (type === 'custom') {
        const custom = toNumber(settings?.customDailyHours);
        if (custom !== null && custom > 0) return Number(custom.toFixed(2));
    }
    return scheduleNetHours(settings, global);
};

// Días laborables del workspace (0 = domingo). Los demás salen como
// "día no laboral" en el historial y no cuentan como falta.
const getWorkDays = (global) => {
    const list = global?.workDays;
    return Array.isArray(list) && list.length > 0 ? list : DEFAULT_WORK_DAYS;
};
const isWorkingDay = (date, global) => getWorkDays(global).includes(date.getDay());

// Los instructores dictan clases, no fichan: no están en planilla y por tanto
// no tienen horario, ni faltas, ni descuentos. Quien además es colaborador
// (o admin) sí cuenta como personal.
const isInstructorOnly = (u) => !!u?.isInstructor && !u?.isCollaborator && u?.role !== 'admin';
const isPayroll = (u) => !isInstructorOnly(u);

// Quien entra en planilla.
//
// Una persona puede ser colaborador e instructor a la vez, asi que la marca de
// instructor no basta: solo decide el valor POR DEFECTO (los instructores
// quedan fuera). Lo que manda es el interruptor "En planilla" de su ficha,
// guardado en overtime_settings.inPayroll.
const defaultInPayroll = (u) => !u?.isInstructor;
const isInPayroll = (u, settings) => {
    if (typeof settings?.inPayroll === 'boolean') return settings.inPayroll;
    return defaultInPayroll(u);
};

// Colaboradores dados de baja: rechazados, suspendidos o que nunca llegaron a
// aprobarse. No fichan, no salen en la planilla y no se les detectan faltas.
// Un usuario sin `status` es anterior a que existiera el campo: se asume
// activo para no hacerlo desaparecer de golpe.
const INACTIVE_STATUSES = ['pending', 'rejected', 'inactive', 'suspended', 'despedido'];
const isActiveUser = (u) => !INACTIVE_STATUSES.includes(u?.status);

// Tarifa/hora sugerida = sueldo ÷ (30 días × horas efectivas por día).
const computeHourlyRate = (salary, dailyHours) =>
    (salary > 0 && dailyHours > 0) ? Number((salary / (30 * dailyHours)).toFixed(2)) : 0;

// Minutos que realmente duró el refrigerio, si se marcaron la salida y el
// regreso. Devuelve null cuando no se marcó, para poder distinguir "no marcó"
// de "se tomó 0 minutos".
const takenBreakMinutes = (record) => {
    if (!record?.breakStart || !record?.breakEnd) return null;
    return Math.round(calculateHours(record.breakStart, record.breakEnd) * 60);
};

// Minutos de más sobre el refrigerio permitido. Sólo cuenta si se marcó:
// quien no marca refrigerio se rige por el valor configurado y no excede nada.
const breakExcessMinutes = (record, allowedMinutes) => {
    const taken = takenBreakMinutes(record);
    if (taken === null) return 0;
    return Math.max(0, taken - (allowedMinutes || 0));
};

// El refrigerio que se descuenta de las horas trabajadas: el real si se marcó,
// si no el configurado.
const effectiveBreakMinutes = (record, allowedMinutes) => {
    const taken = takenBreakMinutes(record);
    return taken === null ? (allowedMinutes || 0) : taken;
};

// Horas realmente trabajadas: lo marcado menos el refrigerio, nunca negativo.
const netWorkedHours = (entryTime, exitTime, breakMinutes) => {
    if (!entryTime || !exitTime) return 0;
    const net = calculateHours(entryTime, exitTime) - (breakMinutes || 0) / 60;
    return Number((net > 0 ? net : 0).toFixed(2));
};

const calcLateMinutes = (entryTime, scheduledEntry = DEFAULT_ENTRY) => {
    if (!entryTime) return 0;
    const [h, m] = entryTime.split(':').map(Number);
    const [sh, sm] = scheduledEntry.split(':').map(Number);
    const diff = (h * 60 + m) - (sh * 60 + sm);
    return diff > 0 ? diff : 0;
};

// Minutos que un colaborador marco su entrada ANTES de la hora pactada.
// Es el espejo de calcLateMinutes: la tardanza descuenta, la entrada
// adelantada suma como hora extra (ver calcOvertimeMinutes, que hace lo
// mismo del lado de la salida).
const calcEarlyEntryMinutes = (entryTime, scheduledEntry = DEFAULT_ENTRY) => {
    if (!entryTime) return 0;
    const [h, m] = entryTime.split(':').map(Number);
    const [sh, sm] = scheduledEntry.split(':').map(Number);
    const diff = (sh * 60 + sm) - (h * 60 + m);
    return diff > 0 ? diff : 0;
};

const calcEarlyExitMinutes = (exitTime, scheduledExit = DEFAULT_EXIT) => {
    if (!exitTime) return 0;
    const [h, m] = exitTime.split(':').map(Number);
    const [sh, sm] = scheduledExit.split(':').map(Number);
    const diff = (sh * 60 + sm) - (h * 60 + m);
    return diff > 0 ? diff : 0;
};

const calcOvertimeMinutes = (exitTime, scheduledExit = DEFAULT_EXIT) => {
    if (!exitTime) return 0;
    const [h, m] = exitTime.split(':').map(Number);
    const [sh, sm] = scheduledExit.split(':').map(Number);
    const diff = (h * 60 + m) - (sh * 60 + sm);
    return diff > 0 ? diff : 0;
};

// Horario irregular (settings.scheduleMode === 'flexible'): no hay una
// entrada/salida pactada contra la cual medir tardanza o salida temprana —
// la persona puede marcar a la hora que quiera. Lo unico que importa es si
// completo sus horas del dia: si le faltan, se descuentan igual que una
// salida temprana; si se pasa, cuenta como hora extra igual que una salida
// tardia. Se reusan esos mismos campos (earlyExitMinutes/overtimeMinutes)
// para que el resto del modulo (descuentos, nomina) no tenga que saber que
// el horario de esta persona es distinto.
const calcFlexibleDayMinutes = (workedHours, workHrsPerDay) => {
    const workedMin = Math.round((workedHours || 0) * 60);
    const targetMin = Math.round((workHrsPerDay || 0) * 60);
    const diff = workedMin - targetMin;
    return {
        shortfallMinutes: diff < 0 ? -diff : 0,
        overtimeMinutes: diff > 0 ? diff : 0,
    };
};

export {
    DEFAULT_ENTRY, DEFAULT_EXIT, DEFAULT_WORK_HOURS,
    DEFAULT_BREAK_MINUTES, DEFAULT_MIN_WAGE, DEFAULT_WORK_DAYS,
    DAY_LABELS, DAY_INITIALS, WORKDAY_TYPES,
    toNumber, calculateHours,
    calcLateMinutes, calcEarlyEntryMinutes, calcEarlyExitMinutes, calcOvertimeMinutes,
    calcFlexibleDayMinutes,
    getBreakMinutes, scheduleNetHours, effectiveDailyHours,
    getWorkDays, isWorkingDay,
    getPeruHolidays, getHolidayName, computeHolidayWork,
    isInstructorOnly, isPayroll, isActiveUser, INACTIVE_STATUSES,
    defaultInPayroll, isInPayroll,
    computeHourlyRate,
    takenBreakMinutes, breakExcessMinutes, effectiveBreakMinutes,
    netWorkedHours,
};

// ── Calculo de un periodo para un colaborador ──────────────────────────────
// Unica fuente de verdad del dinero: la usan el Resumen de Control de Personal
// y la Nomina. Devuelve el desglose completo del periodo.
//
// `attendanceRecords` deben venir ya enriquecidos (workedHours netas y
// breakExcessMinutes), tal como los deja el memo de OvertimeCalculator.
export const computeEmployeePeriod = ({
    user,
    settings = {},
    global = {},
    attendanceRecords = [],   // todos los del colaborador, de cualquier fecha
    overtimeRecords = [],     // todos los del colaborador
    solicitudes = [],         // para respetar justificaciones aprobadas
    start,                    // 'yyyy-MM-dd'
    end,                      // 'yyyy-MM-dd'
    fullMonth = true,         // si el periodo es un mes completo, el sueldo va entero
}) => {
    const inRange = (d) => d >= start && d <= end;
    const userAtt = attendanceRecords.filter(r => r.userId === user.id && inRange(r.date));
    const userOT = overtimeRecords.filter(r => r.userId === user.id && inRange(r.date));

    const salaryBase = settings.monthlySalary || 0;
    const hourlyRate = settings.hourlyRate || 0;
    const workHrsPerDay = effectiveDailyHours(settings, global) || DEFAULT_WORK_HOURS;
    const schedEntry = settings.scheduledEntry || DEFAULT_ENTRY;
    const schedExit = settings.scheduledExit || DEFAULT_EXIT;

    const allDays = eachDayOfInterval({ start: parseISO(start), end: parseISO(end) });
    // Los feriados no son días exigibles: no cuentan como laborables ni como falta.
    const workDays = allDays.filter(d => isWorkingDay(d, global) && !getHolidayName(format(d, 'yyyy-MM-dd'), global)).length;

    const totalWorkedHrs = userAtt.reduce((a, c) => a + (c.workedHours || 0), 0);
    const totalLateMin = userAtt.reduce((a, c) => a + (c.lateMinutes || 0), 0);
    const totalEarlyMin = userAtt.reduce((a, c) => a + (c.earlyExitMinutes || 0), 0);
    const totalBreakExcessMin = userAtt.reduce((a, c) => a + (c.breakExcessMinutes || 0), 0);
    const lateCount = userAtt.filter(r => (r.lateMinutes || 0) > 0).length;
    const totalOTHours = userOT.reduce((a, c) => a + (c.hours || 0), 0);
    const totalOTPay = userOT.reduce((a, c) => a + (c.totalPay || 0), 0);

    // Faltas: las registradas a mano y las detectadas por ausencia de marcacion.
    const allFaltaRecords = userAtt.filter(r => r.status === 'falta');
    // Un feriado no trabajado se paga: una falta registrada ese día no descuenta.
    const faltaRecords = allFaltaRecords.filter(r => !getHolidayName(r.date, global));
    const approved = new Set(
        solicitudes
            .filter(s => s.type === 'justificacion' && s.status === 'aprobada' && s.date && s.userId === user.id)
            .map(s => s.date)
    );
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const firstEverDate = attendanceRecords
        .filter(r => r.userId === user.id)
        .reduce((min, r) => (!min || r.date < min ? r.date : min), null);
    const datesWithRecord = new Set(userAtt.map(r => r.date));

    const detectedFaltaDates = [];
    const justifiedFaltaDates = [];
    const holidaysOff = allFaltaRecords
        .filter(r => getHolidayName(r.date, global))
        .map(r => ({ date: r.date, name: getHolidayName(r.date, global) }));
    if (firstEverDate) {
        for (const d of allDays) {
            const ds = format(d, 'yyyy-MM-dd');
            if (ds >= todayStr) continue;         // hoy todavia puede marcar
            if (ds < firstEverDate) continue;     // antes de entrar a la empresa
            if (!isWorkingDay(d, global)) continue;
            if (datesWithRecord.has(ds)) continue;
            const holidayName = getHolidayName(ds, global);
            if (holidayName) { holidaysOff.push({ date: ds, name: holidayName }); continue; }
            if (approved.has(ds)) { justifiedFaltaDates.push(ds); continue; }
            detectedFaltaDates.push(ds);
        }
    }
    const faltasCount = faltaRecords.length + detectedFaltaDates.length;
    const daysWorked = userAtt.length - allFaltaRecords.length;
    holidaysOff.sort((a, b) => b.date.localeCompare(a.date));

    // Descuento proporcional: cada minuto perdido vale lo que vale un minuto
    // de un dia de 30 — el mismo criterio legal que ya usa computeHourlyRate
    // (sueldo / (30 * horas del dia)) y que el prorateo de `salary` de abajo.
    //
    // Antes se dividia entre `workDays` (los dias laborables del periodo,
    // SIN domingos) en vez de 30. Un mes con 4 domingos tiene 26 dias
    // laborables, no 30, asi que el minuto salia 30/26 ≈ 15% mas caro: una
    // falta de un sueldo de S/1500 costaba S/57.69 (1500/26) en vez de S/50
    // (1500/30), y lo mismo de mas en cada tardanza y salida temprana.
    const basisDays = fullMonth ? 30 : allDays.length;
    const expectedMinutes = basisDays * workHrsPerDay * 60;
    const lostMinutes = totalLateMin + totalEarlyMin + totalBreakExcessMin + faltasCount * workHrsPerDay * 60;
    const salary = fullMonth ? salaryBase : (salaryBase / 30) * allDays.length;
    const discount = expectedMinutes > 0 ? (lostMinutes / expectedMinutes) * salary : 0;
    const dayRate = basisDays > 0 ? salary / basisDays : 0;
    const { items: holidaysWorked, total: holidayPay } = computeHolidayWork({ records: userAtt, global, workHrsPerDay, dayRate });
    const netPay = salary - discount + totalOTPay + holidayPay;
    // Se exponen para que cualquier desglose por fila (tardanza/salida/falta
    // individual en la UI) use la MISMA base de 30 dias que el `discount`
    // de arriba, en vez de recalcular con `workDays` por su cuenta — que es
    // justo el bug que se corrigio aqui.
    const perMinuteRate = expectedMinutes > 0 ? salary / expectedMinutes : 0;

    return {
        user, salary, hourlyRate, workHrsPerDay, schedEntry, schedExit,
        isFlexible: settings.scheduleMode === 'flexible',
        workDays, daysWorked, totalWorkedHrs, basisDays, perMinuteRate, dayRate,
        totalLateMin, totalEarlyMin, totalBreakExcessMin, lateCount,
        faltasCount, faltaRecords, detectedFaltaDates, justifiedFaltaDates,
        totalOTHours, totalOTPay,
        holidaysWorked, holidaysOff, holidayPay,
        discount, netPay,
        overtimeRecords: userOT, attendanceRecords: userAtt,
    };
};

// Desglose de UN dia, para mostrar como se llego a cada monto. No calcula con
// otra formula: usa las mismas tarifas que computeEmployeePeriod y lo ya
// derivado en los registros, asi la calculadora no puede contradecir al Resumen.
//
// El refrigerio no tomado se devuelve aparte y NO entra al total: es informativo.
// El dinero del dia sale del reloj contra el horario pactado, y ese horario ya
// reserva el refrigerio sin pago, lo haya tomado la persona o no.
export const computeDayBreakdown = ({
    att = null,               // registro de asistencia del dia (enriquecido)
    otRecords = [],           // horas extra de ese dia
    isFalta = false,
    perMinuteRate = 0,
    dayRate = 0,
    isFlexible = false,
    holidayPay = 0,           // pago adicional si ese día es feriado trabajado
}) => {
    const discounts = [];
    if (isFalta) {
        discounts.push({ kind: 'falta', amount: dayRate });
    } else if (att) {
        if (att.lateMinutes > 0) discounts.push({ kind: 'late', minutes: att.lateMinutes, amount: att.lateMinutes * perMinuteRate });
        if (att.earlyExitMinutes > 0) discounts.push({ kind: 'early', minutes: att.earlyExitMinutes, amount: att.earlyExitMinutes * perMinuteRate });
        if (att.breakExcessMinutes > 0) discounts.push({ kind: 'break', minutes: att.breakExcessMinutes, amount: att.breakExcessMinutes * perMinuteRate });
    }
    const discountTotal = discounts.reduce((a, d) => a + d.amount, 0);
    const otTotal = otRecords.reduce((a, r) => a + (r.totalPay || 0), 0);

    let lunch = null;
    if (att?.entryTime && att?.exitTime && !isFalta) {
        const allowed = att.breakAllowedMinutes || 0;
        const taken = att.breakTakenMinutes ?? null;
        // En horario irregular el refrigerio marcado ya ajusta las horas netas,
        // asi que lo no tomado si se le reconoce. Sin marcar, se asume el configurado.
        const unrecognized = taken === null
            ? allowed
            : (isFlexible ? 0 : Math.max(0, allowed - taken));
        lunch = {
            allowed,
            taken,
            unrecognizedMinutes: unrecognized,
            unrecognizedValue: unrecognized * perMinuteRate,
            grossMinutes: Math.round((att.grossWorkedHours || 0) * 60),
            netMinutes: Math.round((att.workedHours || 0) * 60),
        };
    }

    return { discounts, discountTotal, otTotal, holidayPay, dayNet: dayRate - discountTotal + otTotal + holidayPay, lunch };
};

// Rango de fechas de un mes 'yyyy-MM'.
export const monthRange = (period) => {
    const [y, m] = period.split('-').map(Number);
    return {
        start: format(startOfMonth(new Date(y, m - 1)), 'yyyy-MM-dd'),
        end: format(endOfMonth(new Date(y, m - 1)), 'yyyy-MM-dd'),
    };
};
